/**
 * THE PUBLIC-TABLE LEAVE PENALTY — pure arithmetic, shared by the game-server (which imposes it)
 * and the web (which renders it). No clock, no I/O: every function takes `now`.
 *
 * ── The rule (operator, 2026-08-27) ──────────────────────────────────────────
 * Leaving a PUBLIC online game IN PROGRESS is a recorded loss. Repeat it and you are locked out
 * of public online games for an hour at a time, on a shrinking allowance:
 *
 *     3 leaves  → 1 hour     (tier 1)
 *     2 more    → 1 hour     (tier 2, at a cumulative 5)
 *     1 more    → 1 hour     (tier 3, at a cumulative 6)
 *     every further leave in the window → another hour
 *
 * The 24-hour window is measured from the player's FIRST leave, NOT from each lockout; when it
 * expires the counter resets to the FULL three-leave allowance. The tail (one hour per further
 * leave) is self-limiting: a locked player cannot enter a public game, so they cannot accumulate
 * leaves while serving one.
 *
 * ⚠️ A DISCONNECT IS NOT A LEAVE and never reaches this module — that is still true, and it is the
 * whole reason Colyseus's `consented` flag must NOT be used for this (a graceful shutdown sets it
 * for everyone; see `online-room.ts`'s `leave-forever` handler).
 *
 * ⚠️ BUT «ONLY THE APP'S EXPLICIT EXIT REACHES THIS MODULE» STOPPED BEING TRUE ON 2026-09-21, AND
 * THIS COMMENT SAID IT FOR A WHILE AFTERWARDS. There are TWO doors now:
 *   ① «خروج بدون بازگشت» — the client's `leave-forever` MESSAGE (`applyPublicLeavePenalty`);
 *   ② an IDLE REMOVAL at a PUBLIC table for two — `TableController.removeForIdling` →
 *      `onLeaveCounted` → `LeavePenaltyService.recordLeave`, which is decided INSIDE the
 *      controller and sends no message, so it carries the deploy protection as its own
 *      `isShuttingDown()` guard instead of riding the message's.
 * Anything that counts the doors — or words the refusal below — must account for both.
 *
 * ⚠️ THE SENTENCE AND ITS DETECTOR LIVE IN THIS ONE FILE, deliberately. The refusal is thrown from
 * `onAuth` and surfaced verbatim by Colyseus, so the client can only recognise it by its text —
 * exactly like `SeatExpiredError`'s message match. Keeping {@link leaveLockoutMessage} and
 * {@link isLeaveLockoutMessage} together means a reworded server sentence can never leave the
 * client unable to spot it (a round-trip test pins that).
 */

/** How long a player's leave history is remembered, measured from their FIRST leave. */
export const LEAVE_WINDOW_MS = 24 * 60 * 60 * 1000;

/** How long each lockout lasts. */
export const LEAVE_LOCKOUT_MS = 60 * 60 * 1000;

/** Leaves allowed before each successive lockout: 3, then 2, then 1… */
export const LEAVE_ALLOWANCES: readonly number[] = [3, 2, 1];

/** …and 1 more for every lockout beyond the third (the operator's call for the tail). */
export const LEAVE_REPEAT_ALLOWANCE = 1;

/** One player's rolling leave record. Persisted as `PublicLeavePenalty`; `null` = never left. */
export interface LeavePenaltyState {
  /** Epoch ms of the FIRST leave of the current window. */
  readonly windowStartedAt: number;
  /** Leaves inside the current window. */
  readonly leaveCount: number;
  /** Lockouts already imposed inside the current window (0 ⇒ the next one is tier 1). */
  readonly lockoutCount: number;
  /** Epoch ms until which public online games are barred, or null. */
  readonly lockedUntil: number | null;
}

/** The outcome of recording one leave. */
export interface LeaveOutcome {
  readonly state: LeavePenaltyState;
  /** Epoch ms of a lockout imposed BY THIS leave, or null when it imposed none. */
  readonly lockedUntil: number | null;
}

/**
 * The cumulative leave count at which lockout number `lockoutCount + 1` fires.
 * `leaveThreshold(0)` = 3 (the first), then 5, 6, 7, 8… — the prefix sums of
 * {@link LEAVE_ALLOWANCES}, extended by {@link LEAVE_REPEAT_ALLOWANCE}.
 */
export function leaveThreshold(lockoutCount: number): number {
  let total = 0;
  for (let i = 0; i <= lockoutCount; i++) total += LEAVE_ALLOWANCES[i] ?? LEAVE_REPEAT_ALLOWANCE;
  return total;
}

/**
 * Record one leave. TOTAL — it is safe to call with any state at any time (a leave landing while
 * a lockout is already being served still counts toward the window, and never shortens the
 * standing lock).
 */
export function applyLeave(state: LeavePenaltyState | null, now: number): LeaveOutcome {
  const expired = state === null || now - state.windowStartedAt >= LEAVE_WINDOW_MS;
  /*
   * A WINDOW RESET MUST NOT HAND BACK AN HOUR ALREADY IMPOSED. A lockout minted just before the
   * 24h boundary outlives the window it belongs to; carrying `lockedUntil` across the reset is
   * what makes "an imposed hour is served in full" true rather than a race.
   */
  const carriedLock =
    state && state.lockedUntil !== null && state.lockedUntil > now ? state.lockedUntil : null;
  const base: LeavePenaltyState = expired
    ? { windowStartedAt: now, leaveCount: 0, lockoutCount: 0, lockedUntil: carriedLock }
    : state;

  const leaveCount = base.leaveCount + 1;
  // `>=`, not `===`: a count that somehow overshoots its threshold must still lock.
  const locks = leaveCount >= leaveThreshold(base.lockoutCount);
  if (!locks) {
    return { state: { ...base, leaveCount }, lockedUntil: null };
  }
  const lockedUntil = now + LEAVE_LOCKOUT_MS;
  return {
    state: {
      windowStartedAt: base.windowStartedAt,
      leaveCount,
      lockoutCount: base.lockoutCount + 1,
      // Never shorten a standing lock (a second lock inside the first hour extends, never trims).
      lockedUntil: Math.max(lockedUntil, base.lockedUntil ?? 0),
    },
    lockedUntil,
  };
}

/** Milliseconds left on a live lockout; 0 when there is none (or it has expired). */
export function lockoutRemainingMs(state: LeavePenaltyState | null, now: number): number {
  if (!state || state.lockedUntil === null) return 0;
  return Math.max(0, state.lockedUntil - now);
}

const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";

function faNum(n: number): string {
  return String(n).replace(/\d/g, (d) => FA_DIGITS[Number(d)] ?? d);
}

/**
 * The stable head of the refusal sentence. {@link isLeaveLockoutMessage} matches on it, so the
 * REST of the sentence may be reworded freely; changing THIS is what the round-trip test guards.
 *
 * ⚠️ IT USED TO SAY «ترک» — «BECAUSE YOU LEFT PUBLIC GAMES» — AND SINCE 2026-09-21 THAT IS FALSE
 * FOR ONE OF THE TWO DOORS THAT FEED THIS COUNTER. An idle removal at a PUBLIC two-seat table now
 * steps it too ({@link LEAVE_ALLOWANCES}, via `TableController.removeForIdling`), and the player
 * it removes did not leave anything: they are sitting at the table reading «چند نوبت پشت‌سرهم بازی
 * نکردی» on their own end card. Telling them an hour later that they are barred «for leaving» is
 * the same defect `GameResult.reason` exists to prevent, one layer down — a constant sentence per
 * outcome, written when the outcome had one cause.
 *
 * So the head is PASSIVE and says only what both doors share: a public game in progress ended
 * without them finishing it. It accuses them of no particular act, because the two acts differ.
 */
export const LEAVE_LOCKOUT_PREFIX = "⛔️ به‌خاطر نیمه‌تمام ماندن چند بازی عمومی";

/**
 * …and the sentence this replaced, still RECOGNISED so a deploy has no window.
 *
 * ⚠️ THE DETECTOR AND THE SENTENCE SHIP IN TWO DIFFERENT IMAGES. `@bing/shared` is bundled into
 * the web AND compiled into the game-server, and the two are recreated minutes apart — so for
 * that gap one side has the new wording and the other the old matcher. A client that fails to
 * recognise the refusal falls through to the GENERIC error screen, which is a worse dialog for a
 * message it could have rendered properly. Matching both costs one line and removes the gap
 * whichever order the two services go in.
 *
 * ⏭ Deletable once prod has been running both halves long enough that no client can still hold
 * the old bundle — it is dead the moment the game-server stops emitting it.
 */
const LEAVE_LOCKOUT_PREFIX_LEGACY = "⛔️ به‌خاطر ترک بازی‌های عمومی";

/** How long is left, in Persian, rounded UP so the message never under-promises. */
function faRemaining(remainingMs: number): string {
  const minutes = Math.ceil(Math.max(0, remainingMs) / 60_000);
  if (minutes <= 0) return "کمتر از یک دقیقه";
  if (minutes >= 60) return `${faNum(Math.round(minutes / 60))} ساعت`;
  if (minutes === 1) return "کمتر از یک دقیقه";
  return `${faNum(minutes)} دقیقه`;
}

/** The join refusal a locked-out player sees (thrown from `onAuth`, surfaced verbatim). */
export function leaveLockoutMessage(remainingMs: number): string {
  return `${LEAVE_LOCKOUT_PREFIX}، تا ${faRemaining(remainingMs)} دیگر نمی‌توانی بازی آنلاین عمومی شروع کنی.`;
}

/** Whether a join error is THIS refusal (so the client can show its own dialog, not the generic one).
 *  Accepts the previous wording too — see {@link LEAVE_LOCKOUT_PREFIX_LEGACY} for why. */
export function isLeaveLockoutMessage(message: unknown): boolean {
  if (typeof message !== "string") return false;
  return (
    message.startsWith(LEAVE_LOCKOUT_PREFIX) || message.startsWith(LEAVE_LOCKOUT_PREFIX_LEGACY)
  );
}
